import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit
} from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { LocalizationInfo } from 'src/app/interfaces/localization-info';
import { OperatingStateOption } from 'src/app/interfaces/operating-state-option';

// Listed after the states in "All other states" rather than mixed in.
const US_TERRITORY_ABBREVIATIONS = new Set([
  'AS',
  'FM',
  'GU',
  'MH',
  'MP',
  'PR',
  'PW',
  'UM',
  'VI'
]);

@Component({
  selector: 'app-operating-states-modal',
  templateUrl: './operating-states-modal.component.html',
  styleUrl: './operating-states-modal.component.less',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class OperatingStatesModalComponent implements OnInit {
  constructor(private activeModal: NgbActiveModal) {}

  @Input() localizationInfo!: LocalizationInfo;
  @Input() options: OperatingStateOption[] = [];
  @Input() selected: OperatingStateOption[] = [];

  filterText = '';
  selectedIds = new Set<number>();

  private clientDivisions: OperatingStateOption[] = [];
  private otherDivisions: OperatingStateOption[] = [];

  get singular(): string {
    return this.localizationInfo.territory.label.toLowerCase();
  }

  get plural(): string {
    return this.localizationInfo.territoryPlural.label.toLowerCase();
  }

  get visibleClientDivisions(): OperatingStateOption[] {
    return this.clientDivisions.filter((division) => this.matches(division));
  }

  get visibleOtherDivisions(): OperatingStateOption[] {
    return this.otherDivisions.filter((division) => this.matches(division));
  }

  ngOnInit(): void {
    this.selectedIds = new Set(this.selected.map(({ id }) => id));

    const byName = (
      left: OperatingStateOption,
      right: OperatingStateOption
    ): number => left.name.localeCompare(right.name);
    const isTerritory = ({ abbreviation }: OperatingStateOption): boolean =>
      US_TERRITORY_ABBREVIATIONS.has(abbreviation);

    this.clientDivisions = this.options
      .filter(({ clientNames }) => clientNames.length > 0)
      .sort(byName);

    const others = this.options
      .filter(({ clientNames }) => clientNames.length === 0)
      .sort(byName);

    this.otherDivisions = [
      ...others.filter((division) => !isTerritory(division)),
      ...others.filter(isTerritory)
    ];
  }

  toggle(division: OperatingStateOption): void {
    if (this.selectedIds.has(division.id)) {
      this.selectedIds.delete(division.id);
    } else {
      this.selectedIds.add(division.id);
    }
  }

  clear(): void {
    this.selectedIds.clear();
  }

  cancel(): void {
    this.activeModal.close(null);
  }

  done(): void {
    this.activeModal.close(
      this.options.filter(({ id }) => this.selectedIds.has(id))
    );
  }

  private matches(division: OperatingStateOption): boolean {
    const text = this.filterText.trim().toLowerCase();

    return (
      text === '' ||
      division.name.toLowerCase().includes(text) ||
      division.abbreviation.toLowerCase() === text
    );
  }
}
